import { Box, Button, Typography } from '@mui/material';
import { FormikContextType, useFormikContext } from 'formik';
import { compact, concat, filter, includes, map, sortBy, values } from 'lodash';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';

import { requisitesApi, shopsApi, counterpartiesApi } from 'api';
import {
  DataWrapper,
  EntityDetailsPage,
  FormikTextField,
  ImportListDialog,
  RequisitesInfo,
  FormikRadioGroup,
  ShopSelect,
  FormikArray,
  FormikSelectProps,
  FormikSelect,
  FormikSelectOption,
} from 'components';
import { NEW_ID } from 'constants/common.constants';
import { ROUTE_PATH } from 'constants/routes';
import { QueryKey, RequisitesDistributionMode } from 'enums';
import { useShopsQuery, useUser } from 'hooks';
import { TranslationNamespace } from 'i18n';
import {
  Counterparty,
  CounterpartyCustomer,
  CounterpartyRequisites,
} from 'types';

type RequisitesSelectProps = {
  name: string;
  label: string;
  options: FormikSelectOption[];
  exclude?: string[];
} & Partial<FormikSelectProps>;

const RequisitesSelect: React.FC<RequisitesSelectProps> = ({
  name,
  label,
  options = [],
  exclude = [],
  ...rest
}) => {
  const availableOptions = useMemo(
    () => filter(options, (option) => !includes(exclude, option.value)),
    [options, exclude],
  );
  return (
    <FormikSelect
      noneOption
      label={label}
      {...rest}
      name={name}
      options={availableOptions}
      sx={{ mr: 1, flex: 1, minWidth: 400 }}
    />
  );
};

const RequisitesForm = () => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'features.counterparties',
  });

  const {
    values: { shopId, requisites },
  } = useFormikContext<Partial<Counterparty>>();

  const queryResultRequisites = useQuery(
    [QueryKey.Requisites, shopId],
    () => requisitesApi.getAllForCounterparties(shopId!),
    { enabled: !!shopId },
  );

  const requisitesOptions = useMemo(
    () =>
      map(queryResultRequisites.data, (requisite) => ({
        value: requisite.id,
        label: (
          <RequisitesInfo
            compact
            canCopy={false}
            requisite={requisite}
            showTrader
          />
        ),
      })),
    [queryResultRequisites.data],
  );

  const renderRequisitesSelect = useCallback(
    ({ item, index }: { item: CounterpartyRequisites; index: number }) => {
      const exclude = map(
        filter(requisites, (r) => r.requisitesId !== item.requisitesId),
        (t) => t.requisitesId,
      );

      return (
        <RequisitesSelect
          name={`requisites[${index}].requisitesId`}
          label={t('fields.requisites')}
          exclude={exclude}
          options={requisitesOptions}
          required
        />
      );
    },
    [t, requisitesOptions, requisites],
  );

  return (
    <div className="tw-pb-4">
      <DataWrapper
        queryResult={queryResultRequisites}
        emptyViewText={t('fields.no_requisites')}
      >
        <FormikArray
          addItemLabel={t('buttons.add_requisites')}
          name="requisites"
          sortable
          removable
          defaultValue={''}
          ItemComponent={renderRequisitesSelect}
        />
      </DataWrapper>
    </div>
  );
};

export const CounterpartyDetails: React.FC = () => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'features.counterparties',
  });
  const { id } = useParams();
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);
  const { role, isAdmin, isTechOperator } = useUser();

  const [importOpen, setImportOpen] = useState(false);

  const queryResultShops = useShopsQuery(
    QueryKey.Shops,
    shopsApi.getAllAsRole(role),
  );

  const initialValues: Partial<Counterparty> = useMemo(
    () => ({
      shopId: '',
      requisites: [],
      customers: [],
      requisitesDistributionMode: RequisitesDistributionMode.Random,
    }),
    [],
  );

  const isNew = useMemo(() => id === NEW_ID, [id]);

  const listUrl = useMemo(() => {
    if (isTechOperator) {
      return ROUTE_PATH.TECH_OPERATOR.COUNTERPARTIES;
    } else if (isAdmin) {
      return ROUTE_PATH.ADMIN.COUNTERPARTIES;
    }
  }, [isAdmin, isTechOperator]);

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        shopId: Yup.string().required(tCommon('errors.required')),
        requisitesDistributionMode: Yup.string().optional().nullable(),
        requisites: Yup.array().of(
          Yup.object().shape({
            requisitesId: Yup.string().required(tCommon('errors.required')),
          }),
        ),
        customers: Yup.array().of(
          Yup.object().shape({
            customerId: Yup.string().required(tCommon('errors.required')),
          }),
        ),
      }),
    [tCommon],
  );

  const initialValuesSelect = useCallback(
    (item: Counterparty): Counterparty => ({
      ...item,
      requisites: sortBy(item.requisites, 'priority'),
      customers: item.customers || [],
    }),
    [],
  );

  const formatData = useCallback(
    (data: Counterparty) => ({
      ...data,
      requisites: map(data.requisites, (item) => ({
        id: item.id,
        requisitesId: item.requisitesId,
      })),
      customers: map(
        compact(map(data.customers, 'customerId')),
        (customerId) => ({ customerId }),
      ),
    }),
    [],
  );

  const onBeforeUpdateMutation = useCallback(
    ({
      id,
      data,
    }: {
      id: string;
      data: Counterparty;
    }): { id: string; data: Counterparty } => ({
      id,
      data: formatData(data),
    }),
    [formatData],
  );

  const renderCustomerItem = useCallback(
    ({ index }: { index: number }) => (
      <FormikTextField
        name={`customers[${index}].customerId`}
        label={t('fields.customer_id')}
        required
        fullWidth
      />
    ),
    [t],
  );

  const requisitesDistributionModeOptions = useMemo(
    () =>
      map(
        values(RequisitesDistributionMode),
        (mode: RequisitesDistributionMode) => ({
          value: mode,
          label: t(`distribution_modes.${mode}`, { defaultValue: mode }),
        }),
      ),
    [t],
  );

  const handleImportConfirm = useCallback(
    (formik: FormikContextType<Partial<Counterparty>>, ids: string[]) => {
      const existingIds = new Set(map(formik.values.customers, 'customerId'));
      const newCustomers = map(
        filter(ids, (customerId) => !existingIds.has(customerId)),
        (customerId) => ({ customerId } as CounterpartyCustomer),
      );
      formik.setFieldValue(
        'customers',
        concat(formik.values.customers, newCustomers),
      );
      setImportOpen(false);
    },
    [],
  );

  return (
    <EntityDetailsPage
      id={id!}
      api={counterpartiesApi}
      apiAsRole={false}
      listUrl={listUrl}
      title={t(isNew ? 'create.title' : 'edit.title')}
      initialValues={initialValues}
      queryKey={QueryKey.Counterparties}
      initialValuesSelect={initialValuesSelect}
      onBeforeCreateMutation={formatData}
      onBeforeUpdateMutation={onBeforeUpdateMutation}
      validationSchema={validationSchema}
      formClassName="tw-max-w-xl"
    >
      {({ formik }) => (
        <Fragment>
          <ShopSelect
            name="shopId"
            shops={queryResultShops.data}
            required
            disabled={!isNew}
          />
          <FormikRadioGroup
            label={t('fields.distribution_mode')}
            name="requisitesDistributionMode"
            options={requisitesDistributionModeOptions}
          />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 0.5,
            }}
          >
            <Typography variant="subtitle1">{t('fields.customers')}</Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setImportOpen(true)}
            >
              {t('buttons.import_customers')}
            </Button>
          </Box>
          <FormikArray
            addItemLabel={t('buttons.add_customer')}
            name="customers"
            removable
            defaultValue={{ customerId: '' } as CounterpartyCustomer}
            ItemComponent={renderCustomerItem}
          />
          <ImportListDialog
            open={importOpen}
            onClose={() => setImportOpen(false)}
            onConfirm={(ids) => handleImportConfirm(formik, ids)}
          />
          <Typography variant="subtitle1">{t('fields.requisites')}</Typography>
          <RequisitesForm key={formik.values.shopId} />
        </Fragment>
      )}
    </EntityDetailsPage>
  );
};
